<template>
  <div :class=" { debug: debug }">
    <main-nav :isMobile="isMobile" />
    <div class="sectionFixed">
      <div class="mapContainer" :class="{ halfHeight: isMobileIntro, initialView: visibleIntro }">
        <div v-if="loading" class="loading">
          <div class="loader">Loading...</div>
        </div>

        <!-- Annotations and Timeline -->
        <transition name="slide-fade">
          <div v-if="!visibleIntro && !visibleOutro" class="fullWidthContainer bottom">
            <div v-if="isMobile" class="mobileActionsContainer">
              <!-- Mobile Play button and Search -->
              <MobileActions :updateLocation="updateLocation"
                             :showLoading="showLoading"
                             :isPlaying="isPlaying"
                             :play="play"
                             :stop="stop" />
            </div>
            <div class="annotationContainer">
              <AnnotationContent :isMobile="isMobile"
                                 :updateLocation="updateLocation"
                                 :showLoading="showLoading"
                                 :isPlaying="isPlaying"
                                 :play="play"
                                 :stop="stop"
                                 :stateAnnotations="stateAnnotations"
                                 :latestDistrictCode="latestDistrictCode"
                                 :numDistricts="numDistricts"
                                 :totalTimelineSpan="{ start: firstYear, end: lastYear }"
                                 :attribution="attribution" />
            </div>
            <div class="timelineContainer">
              <PlayButton v-if="!isMobile" :isPlaying="isPlaying" :play="play" :stop="stop" />
              <Timeline v-if="dataLoaded" :districts="districtsCollection.features"
                        :currentDistrict="districtJSON"
                        :currentYear="currentYear"
                        :totalTimelineSpan="{ start: firstYear, end: lastYear }"
                        :isPlaying="isPlaying"
                        :stop="stop"
                        :updateYear="updateYear" />
            </div>
          </div>
        </transition>

        <!-- Map -->
        <v-map :zoom="zoom" :center="center" ref="map" :options="mapOptions" @click="onMapClick">
          <v-tilelayer :url="tileUrl" :attribution="attribution"></v-tilelayer>
          <v-attribution :position="attributionPosition" :prefix="attributionPrefix"></v-attribution>
          <v-marker v-if="currentLocation" :lat-lng="currentLocation" :icon="icon"></v-marker>
          <v-geo-json v-if="dataLoaded" :geojson="dataset.geojson" :options="dataset.options"></v-geo-json>
        </v-map>



        <!-- Outro -->
        <transition name="slide-fade">
          <div v-show="visibleOutro" class="outroContainer" :class="outroType">
            <Outro :isMobile="isMobile" :showOutro="showOutro" :hideOutro="hideOutro" :outroType="outroType" :districtIds="districtIds" :firstYear="firstYear" />
          </div>
        </transition>

      </div>
    </div>
    <div v-if="!isMobile" class="sectionScrolling">
      <div class="fixedIntersection"></div>

      <!-- Top waypoint -->
      <intersect @enter.self="showIntro" :threshold="[0]">
        <div class="waypoint top"></div>
      </intersect>

      <!-- Desktop Intro -->
      <div class="introContainer" :class="{ expanded: !skipIntro }">
        <ScrollAwayContent v-if="!loading"
                           :visibleIntro="visibleIntro"
                           :dataLoaded="dataLoaded"
                           :skipIntro="skipIntro"
                           :loadError="loadError"
                           :isMobile="isMobile"
                           :updateLocation="updateLocation"
                           :showLoading="showLoading"
                           :hideIntro="hideIntro"
                           :stateAnnotations="stateAnnotations"
                           :latestDistrictCode="latestDistrictCode"
                           :numDistricts="numDistricts"
                           :totalTimelineSpan="{ start: firstYear, end: lastYear }" />
      </div>

      <div v-if="dataLoaded">
        <!-- Year waypoints -->
        <div class="blocs">
          <intersect v-for="(y, index) in allYearsInReverse" :key="'l_' + y" @enter.self="onEnter" :threshold="[0]" :rootMargin="'0px 0px -97% 0px'">
            <div class="bloc" :id="y">
              {{ y }}
            </div>
          </intersect>
        </div>
        <!-- Bottom waypoint -->
        <intersect @enter.self="showOutro('share')" :threshold="[0]">
          <div class="waypoint bottom"></div>
        </intersect>
      </div>
    </div>
    <div v-else>
      <!-- Mobile Intro -->
      <div v-if="visibleIntro" class="introContainer">
        <ScrollAwayContent v-if="!loading"
                           :dataLoaded="dataLoaded"
                           :skipIntro="skipIntro"
                           :loadError="loadError"
                           :isMobile="isMobile"
                           :updateLocation="updateLocation"
                           :showLoading="showLoading"
                           :hideIntro="play"
                           :stateAnnotations="stateAnnotations"
                           :latestDistrictCode="latestDistrictCode"
                           :numDistricts="numDistricts"
                           :totalTimelineSpan="{ start: firstYear, end: lastYear }" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue2Leaflet from 'vue2-leaflet';
import 'intersection-observer'; // polyfill
import Intersect from 'vue-intersect';
import axios from 'axios';
import intersect from '@turf/intersect';
import area from '@turf/area';
import { interpolate, interpolateAll, combine, separate } from 'flubber';
import { toArray, toGeoJSON } from 'geojson-tools';
import AnnotationContent from './AnnotationContent';
import MobileActions from './MobileActions';
import Timeline from './Timeline';
import ScrollAwayContent from './ScrollAwayContent';
import Outro from './Outro';
import ShareLinks from './ShareLinks';
import annotations from '../assets/data/annotations.json';
import MainNav from './Nav';
import PlayButton from './PlayButton';

export default {
  name: 'DistrictMap',
  components: {
    'v-map': Vue2Leaflet.LMap,
    'v-tilelayer' :Vue2Leaflet.LTileLayer,
    'v-marker': Vue2Leaflet.LMarker,
    'v-geo-json': Vue2Leaflet.LGeoJson,
    'v-attribution' :Vue2Leaflet.LControlAttribution,
    Intersect,
    AnnotationContent,
    MobileActions,
    Timeline,
    ScrollAwayContent,
    Outro,
    ShareLinks,
    MainNav,
    PlayButton,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      startYear: 1960,
      skipIntro: false,

      // New York
      defaultLocation: L.latLng(40.8301446, -73.89679),
      currentLocation: null,
      districtIds: null,
      minSession: 86,

      // animations
      currentYear: 1960,
      districtJSON: {},
      playTimer: null,
      isPlaying: false,
      animationDuration: 250, // (ms) Animation timing (match with .animation on Timeline)
      visibleIntro: true,
      visibleOutro: false,
      outroType: 'share',
      isSmoothScrollSupported: false,

      // results from server
      baseUrl: 'https://us-congress-districts.api.aclu.org/',
      districtsCollection: {},
      stateCode: null,
      latestDistrictCode: null,
      numDistricts: null,
      loadError: false,
      loading: false,

      // Vue2leaflet
      zoom: 4.5,
      center: L.latLng(40.2347, -96.7282),
      mapOptions: { tap: false, zoomSnap: 0.25, attributionControl: false, keyboard: false },
      attributionPosition: 'bottomright',
      attributionPrefix: '',
      icon: L.icon({
        iconUrl: '/static/images/icon-map-pin.svg',
        iconSize:     [16, 16],
        iconAnchor:   [8, 8],
        shadowUrl: '/static/images/icon-map-pin-shadow.svg',
        shadowSize:   [18, 18],
        shadowAnchor: [9, 9],
      }),

      // Mapbox Light
      tilesBaseUrl: 'https://api.tiles.mapbox.com/styles/v1/mapbox/light-v9/tiles/256/{z}/{x}/{y}@2x',
      attribution:'&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>, <a href="https://www.mapbox.com/about/maps/">Mapbox</a>, <a href="https://github.com/JeffreyBLewis/congressional-district-boundaries">Lewis, et al.</a>',
      dataset: {
        geojson: null,
        options: {
          style: {
            color: '#1E55A5',
            fillColor: '#AEDAE2',
            weight: 2,
            fillOpacity: 0.2
          }
        }
      },
    }
  },
  computed: {
    tileUrl() {
      return this.tilesBaseUrl + '?access_token=' + process.env.MAPBOX_TOKEN
    },
    years () {
      const data = this.districtsCollection.features;
      if(!data || data.length == 0) return [];

      return this.getSortedProperties('startYear');
    },
    firstYear () {
      return this.years.length > 0 ? this.years[0] : null;
    },
    lastYear () {
      const data = this.districtsCollection.features;
      if(!data || data.length == 0) return;

      const endYears = this.getSortedProperties('endYear');
      return endYears.reverse()[0];
    },
    allYearsInReverse () {
      if(this.years.length == 0) return [];

      let allYears = [];
      for (var i = this.firstYear; i <= this.lastYear; i++) {
          allYears.push(i);
      }

      return allYears.reverse();
    },
    dataLoaded () {
      return this.dataset.geojson !== null && !this.loadError;
    },
    isMobileIntro () {
      return this.isMobile && this.visibleIntro;
    },
    stateAnnotations () {
      return annotations[this.stateCode];
    }
  },
  methods: {
    onMapClick (e) {
      // hide outro
      if (this.visibleOutro) {
        this.hideOutro();
      }
      // stop playing
      if (this.isPlaying) {
        this.stop();
      }
      if (this.debug) {
        window.setTimeout(() => {
          console.log('onMapClick for testing has .5 second delay');
          this.updateLocation(e.latlng);
        }, 500);
      }
    },

    updateLocation (latlng) {
      // Always show intro screen after updating location
      this.showIntro();
      this.visibleOutro = false;
      this.currentLocation = latlng;
    },

    showIntro () {
      if (this.isPlaying) {
        this.stop()
      }
      this.visibleIntro = true;
    },

    hideIntro () {
      this.visibleIntro = false;
      this.scrollTo(this.currentYear, true);
    },

    showOutro (type) {
      if (this.isPlaying) {
        this.stop()
      }
      this.outroType = (type && type == 'signup') ? 'signup' : 'share';
      this.visibleOutro = true;
    },

    hideOutro () {
      this.visibleOutro = false;

      if (!this.isMobile) {
        this.scrollTo(this.currentYear)
      }
    },

    showLoading (show) {
      if (typeof show === 'undefined') show = true;
      this.loading = show;
    },

    /* Used for IntersectionObserver updates */
    onEnter (el) {
      this.visibleIntro = false;
      this.visibleOutro = false;
      let year = parseInt(el[0].target.id);

      // Update classes
      let blocs = document.getElementsByClassName('blocActive');
      for (let i = 0; i < blocs.length; i++) {
        blocs[i].classList.remove('blocActive');
      }
      el[0].target.classList.add('blocActive');

      // Update data
      this.setDataByYear(year);
    },
    // Because of the reverse timeline, these are indeed backwards
    showNext () {
      let year = this.currentYear - 1;
      if (year < this.firstYear) {
        // Stop on most recent year
        year = this.lastYear;
        this.stop();
      }
      this.updateYear(year);
    },
    showPrev () {
      let year = this.currentYear + 1;
      if (year > this.lastYear) {
        year = this.firstYear;
        this.stop();
      }
      this.updateYear(year);
    },
    updateYear (year) {
      if (this.isMobile) {
        this.setDataByYear(year);
      }
      else {
        // For desktop version:
        // Use scroll to initiate year update in order to
        // keep intersection observer waypoints in sync
        this.scrollTo(year);
      }
    },
    scrollTo (year, smooth = false) {
      const scrollEl = document.getElementById(year);
      if (scrollEl) {
        let top = scrollEl.offsetTop;

        if (year == this.lastYear) {
          // If it's the most recent year, use the bottom offset to account for intro container size
          top += scrollEl.offsetHeight - 10;
        }
        if (smooth && this.isSmoothScrollSupported) {
          window.scrollTo({
            top: top,
            behavior: "smooth"
          });
        }
        else {
          window.scrollTo(0, top);
        }
      }
    },
    // Set play/stop calls here so other components can react to isPlaying
    play () {
      this.visibleIntro = false;
      this.visibleOutro = false;
      this.playTimer = setInterval(this.showNext, this.animationDuration);
      this.isPlaying = true;
    },
    stop () {
      clearInterval(this.playTimer);
      this.isPlaying = false;
    },
    setDataByYear (year) {
      if (year && this.districtsCollection.features
               && year !== this.year
               && year >= this.firstYear
               && year <= this.lastYear) {
        // update districtJSON in order to trigger watcher / animation
        this.districtJSON = this.getDataByYear(year);
        this.currentYear = year;
      }
    },
    getDataByYear (year) {
      let data = this.districtsCollection.features;
      if(!data || data.length == 0) return;

      for (let d in data) {
        if (year >= data[d].properties.startYear && year <= data[d].properties.endYear) {
          return data[d];
        }
      }
      return null;
    },
    fetchDataByLatlng () {
      if (!this.currentLocation) {
        console.error('Location not provided');
        return;
      }

      let url = this.baseUrl + 'pip?min_session=' + this.minSession + '&lat=' + this.currentLocation.lat + '&lng=' + this.currentLocation.lng;
      this.fetchData(url, true);
    },
    fetchDataByIds (ids) {
      if (!this.isValidIds(ids)) {
        console.error('District IDs not provided');
        return;
      }

      let url = this.baseUrl + 'districts?ids=' +ids.join('-');
      this.fetchData(url);
    },
    fetchData (url, skipIntro = false) {
      this.resetState();

      const axiosConfig = {
        timeout: 15000,
      }
      axios.get(url, axiosConfig)
       .then(response => {
        this.initializeData(response.data.results);
        this.skipIntro = skipIntro;
      })
      .catch(error => {
        this.loadError = true;
        this.loading = false;
        console.error(error)
      });
    },
    resetState () {
      this.loadError = false;
      this.loading = true;
      this.skipIntro = false;
      this.stop();

      // Remove previous district shape to avoid situations
      // where initial district shape morphs from previous location
      this.districtJSON = {};
    },
    initializeData (data) {
      const districts = this.buildGeoJSONDistricts(data);
      this.loading = false;

      if (districts.length === 0) {
        this.loadError = true;
        return;
      }

      this.districtsCollection = {
        type: 'FeatureCollection',
        features: districts
      }

      // Set initial data
      this.districtJSON = this.dataset.geojson = districts[0];
      this.stateCode = districts[0].properties.state.toUpperCase();
      this.latestDistrictCode = districts[0].properties.district_num;
      this.numDistricts = districts.length;
      this.currentYear = districts[0].properties.endYear;
      this.loadError = false;

      // Fit map bounds to data
      let no_at_large = districts.filter(d => d.properties.at_large == false);
      let mapbounds;

      // Use custom fitbounds for Alaska
      // (Alaska spans the int'l dateline and forces the map to full world zoom)
      if (this.stateCode == 'AK') {
        mapbounds = [[ 72, -150 ], [ 55, -158 ]];
      }
      // Fit to district collection with no at-large districts
      else if (no_at_large.length > 0) {
        mapbounds = L.geoJSON({ type: 'FeatureCollection', features: no_at_large }).getBounds();
      }
      // Fit to at-large district
      else {
        mapbounds = L.geoJSON(this.districtsCollection).getBounds();
      }

      // Fit bounds
      const paddingOptions = this.isMobile ?
        {
          paddingTopLeft: [5, 30],
          paddingBottomRight: [5, 100]
        } : {
          paddingTopLeft: [200, 0],
          paddingBottomRight: [0, 100]
        }
      this.$refs.map.mapObject.fitBounds(mapbounds, paddingOptions);

      // Update district ids and hash
      this.districtIds = this.getDistrictIds();
    },
    morphDistrict: function(newVal, oldVal) {
      if (!newVal.geometry) {
        return;
      }

      // If initial value or the same geometry, don't animate
      if (!oldVal.geometry || oldVal.geometry.coordinates === newVal.geometry.coordinates) {
        this.dataset.geojson = newVal;
        return;
      }

      let vm = this
      let anim = {};
      const maxMorphDuration = 350;  // Maximum duration of morphing
      anim.startYear = oldVal.properties.startYear;
      anim.years = oldVal.properties.endYear - oldVal.properties.startYear;

      // Time to wait before morphing
      // anim.waitDuration = Math.max((oldVal.properties.endYear - oldVal.properties.startYear) * 200, 75);
      anim.waitDuration = 75;

      // Time during morphing
      anim.morphDuration = 0;
      var areaIntersect = intersect(newVal, oldVal);
      areaIntersect = areaIntersect ? area(areaIntersect) : 0;

      var maxIntersect = 0.99;
      if (areaIntersect / area(oldVal) < maxIntersect || areaIntersect / area(newVal) < maxIntersect) {
          anim.morphDuration = Math.min(Math.max((1 - areaIntersect / area(newVal)), (1 - areaIntersect / area(oldVal))) * 25000, maxMorphDuration);
      }

      anim.startTime = +new Date();

      // Prepare multipolygons as needed
      let oldValAsArray = toArray(oldVal.geometry);
      let newValAsArray = toArray(newVal.geometry);
      let sortedOldVal = null,
          sortedNewVal = null;

      if (oldVal.geometry.type === 'MultiPolygon') {
        sortedOldVal = oldValAsArray.map(p => p).sort((a, b) => area({type: 'Polygon', coordinates: [b]}) - area({type: 'Polygon', coordinates: [a]}));
      }
      if (newVal.geometry.type === 'MultiPolygon') {
        sortedNewVal = newValAsArray.map(p => p).sort((a, b) => area({type: 'Polygon', coordinates: [b]}) - area({type: 'Polygon', coordinates: [a]}));
      }

      // MultiPolygons --> MultiPolygons (with same # of polygons)
      if (sortedOldVal && sortedNewVal && sortedOldVal.length === sortedNewVal.length) {
        anim.interpolator = interpolateAll(
            sortedOldVal,
            sortedNewVal,
            { string: false, single: true, match: true });
      }

      // Polygon --> MultiPolygons (separate)
      else if (oldVal.geometry.type === 'Polygon' && newVal.geometry.type === 'MultiPolygon') {
        anim.interpolator = separate(
            oldValAsArray,
            sortedNewVal,
            { string: false, single: true });
      }
      // MultiPolygons --> Polygon (combine)
      else if (oldVal.geometry.type === 'MultiPolygon' && newVal.geometry.type === 'Polygon') {
        anim.interpolator = combine(
            sortedOldVal,
            newValAsArray,
            { string: false, single: true });
      }

      // Default: morph largest polygon from each
      else {
        anim.interpolator = interpolate(
            oldVal.geometry.type === 'MultiPolygon' ? oldVal.geometry.coordinates.map(p => p).sort((a, b) => area({type: 'Polygon', coordinates: b}) - area({type: 'Polygon', coordinates: a}))[0][0] : oldVal.geometry.coordinates[0],
            newVal.geometry.type === 'MultiPolygon' ? newVal.geometry.coordinates.map(p => p).sort((a, b) => area({type: 'Polygon', coordinates: b}) - area({type: 'Polygon', coordinates: a}))[0][0] : newVal.geometry.coordinates[0],
            { string: false });
      }

      anim.cancel = () => {
          // do something here?
          anim.canceled = true;
      };
      anim.canceled = false;

      // Animation frame
      anim.frame = () => {
          if (anim.canceled ) {
              return;
          }

          var elapsed = ((+new Date()) - anim.startTime);
          var t;

          // Showing oldVal (phase 1)
          if (elapsed <= anim.waitDuration) {
              t = Math.min(elapsed / anim.waitDuration, 1);

              window.requestAnimationFrame(anim.frame);
          }
          // Morphing from district oldVal to newVal (phase 2)
          else if (anim.morphDuration > 0 && elapsed - anim.waitDuration <= anim.morphDuration) {
              t = Math.min((elapsed - anim.waitDuration) / anim.morphDuration, 1);

              anim.shape = anim.interpolator(t);

              // Need to convert MultiPolygons back to geojson
              if (anim.shape[0][0][0]) {
                var F = {
                    type: 'Feature',
                    properties: oldVal.properties,
                    geometry: toGeoJSON([anim.shape], 'MultiPolygon')
                };
              }
              else {
                // Don't bother converting to geojson for polygons
                var F = {
                    type: 'Feature',
                    properties: oldVal.properties,
                    geometry: { type: 'Polygon', coordinates: [anim.shape] }
                };
              }

              vm.dataset.geojson = F;

              window.requestAnimationFrame(anim.frame);
          }
          // Finished with morph
          else {
              vm.dataset.geojson = newVal;
          }
      };
      anim.frame();
    },
    buildGeoJSONDistricts (results) {
      return results.map((row, i) => {
          return {
              type: 'Feature',
              geometry: JSON.parse(row.boundary_simple),
              properties: Object.assign({}, row, {
                  // start year of district
                  startYear: parseInt(1789 - 2 + row.start_session * 2),
                  // end year of district (election year, term ends following year in Jan)
                  endYear: parseInt(1789 + row.end_session * 2) - 1,
              })
          };
      });
    },
    getSortedProperties (property) {
      const data = this.districtsCollection.features;
      if(!data || data.length == 0) return [];

      let list = [];
      for(let d in data){
          list.push( parseInt(data[d].properties[property])  );
      }
      return list.sort();
    },
    getDistrictIds () {
      const data = this.districtsCollection.features;
      if(!data || data.length == 0) return [];

      return this.getSortedProperties('id');
    },
    resetMap () {
      this.resetState();
      this.loading = false;
      this.currentLocation = null;
      this.$refs.map.mapObject.setView(this.center, this.zoom);
    },
    updateURL() {
      const newIdString = this.formatIdString(this.districtIds);
      const currentIdString = this.getUrlParameter('ids');

      if (newIdString && newIdString != currentIdString) {
        // build new URL with updated IDs
        const newUrl = this.getUpdatedUrl('ids', newIdString)

        window.history.pushState({ids: this.districtIds, loc: this.currentLocation}, null, newUrl);
      }
    },
    getUrlParameter(name) {
      name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
      var regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
      var results = regex.exec(location.search)
      return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
    },
    getUpdatedUrl(key, value) {
      // Note: updated URL does not include hash
      const baseUrl = [location.protocol, '//', location.host, location.pathname].join('');
      const urlQueryString = document.location.search;
      const newParam = key + '=' + value;
      let params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (urlQueryString) {
          var updateRegex = new RegExp('([\?&])' + key + '[^&]*');
          var removeRegex = new RegExp('([\?&])' + key + '=[^&;]+[&;]?');

          if( typeof value == 'undefined' || value == null || value == '' ) { // Remove param if value is empty
              params = urlQueryString.replace(removeRegex, "$1");
              params = params.replace( /[&;]$/, "" );

          } else if (urlQueryString.match(updateRegex) !== null) { // If param exists already, update it
              params = urlQueryString.replace(updateRegex, "$1" + newParam);

          } else { // Otherwise, add it to end of query string
              params = urlQueryString + '&' + newParam;
          }
      }

      // no parameter was set so we don't need the question mark
      params = params == '?' ? '' : params;

      return baseUrl + params;
    },
    formatIdString(ids) {
      let querystring = null;
      if (this.isValidIds(ids)) {
        querystring = ids.join('-');
      }
      return querystring;
    },
    parseIdString(querystring) {
      const ids = querystring.split('-');
      if (ids.length && this.isValidIds(ids)) {
        return ids;
      }
      return null;
    },
    // TODO: Add better checking that ids are indeed ids
    isValidIds(ids) {
      if (ids && !isNaN(ids[0])) {
        return true;
      }
      return false;
    }
  },
  watch: {
    // data is fetched when currentLocation is updated
    // or when districtIds are present on initial load
    currentLocation: function (newVal, oldVal) {
      if (newVal) {

        // Do not fetch if we're requesting the exact same lat/lng
        if (oldVal && newVal.lat && oldVal.lat && newVal.lat == oldVal.lat) return;

        this.fetchDataByLatlng();
      }
    },
    districtIds: function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.updateURL();
      }
    },
    districtJSON: function(newVal, oldVal) {
      if (!newVal || !newVal.geometry) {
        // Remove geometry
        this.dataset.geojson = null;
        return;
      }
      this.morphDistrict(newVal, oldVal);
      if (this.debug) console.log('Showing district ', newVal.properties.id);
    }
  },

  // Lifecycle methods
  created: function () {
    // Get location from url query string
    let querystring = this.getUrlParameter('ids');
    if (querystring) {
      const ids = this.parseIdString(querystring);
      if (this.isValidIds(ids)) {
        this.fetchDataByIds(ids);
      }
    }
    // Set listener for popstate
    window.onpopstate = (e) => {
      // If no state saved, reset to intial view
      if (!e.state || !e.state.ids) {
        this.resetMap();
        return;
      }
      // If available, fetch data by latlng so we can retain marker
      // (query will already have been made so data will be locally cached)
      if (e.state.loc) {
        this.currentLocation = e.state.loc;
      }
      else {
        // Otherwise, remove marker and fetch data by ids
        this.currentLocation = null;
        this.fetchDataByIds(e.state.ids);
      }
    };

    // Check if smooth scroll is supported
    this.isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
  },
  mounted: function () {
    // Disable scroll wheel
    this.$refs.map.mapObject.scrollWheelZoom.disable();

    // Scroll to top
    window.scrollTo(0, 0);
  },
  beforeDestroy: function () {
    if(this.playTimer) {
      clearInterval(this.playTimer);
    }
  }
}

</script>

<style lang="scss">
/* Variables */
@import '~styles/variables.scss';

/* Layout */
.sectionFixed {
  position: fixed;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
}
/* Do not remove .sectionScrolling
 * used for IntersectionObserver waypoints
 */
.sectionScrolling {
  background: $darkBackgroundColor;
}
.fullWidthContainer {
  position: fixed;
  left: 5vw;
  right: 5vw;
  width: 90vw;
  z-index: 1000;
}
.fullWidthContainer.top {
  top: 30px;
}
.fullWidthContainer.bottom {
  bottom: 30px;
  z-index: 1001;
}

/* MAP  */
.mapContainer {
  position: relative;
  width: 100%;
  height: 100%;

  .leaflet-control-attribution {
    bottom: 0;
    transition: bottom 0.5s;
  }
  .leaflet-control-zoom {
    margin: 0;
    position: fixed;
    right: 5vw;
    bottom: 120px;
    transition: all $transitionDuration $transitionEasing $transitionDelay;
  }
  // Use same slide-fade transition used by timeline
  &.initialView {
    .leaflet-control-zoom {
      transform: translateY(100px);
      opacity: 0;
      transition: all $transitionDuration $transitionEasing;
    }
  }
}
.loading {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: rgba(255,255,255,0.4);
  font-size: 48px;
  color: $darkTextColor;
}
/* loader animation */
.loader,
.loader:before,
.loader:after {
  border-radius: 50%;
  width: 2em;
  height: 2em;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
  -webkit-animation: load7 1.8s infinite $transitionEasing;
  animation: load7 1.8s infinite $transitionEasing;
}
.loader {
  top: 45%;
  left: 50%;
  color: $accentColor;
  font-size: 10px;
  position: relative;
  text-indent: -9999em;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}
.loader:before,
.loader:after {
  content: '';
  position: absolute;
  top: 0;
}
.loader:before {
  left: -3.5em;
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}
.loader:after {
  left: 3.5em;
}
@-webkit-keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}
@keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}

/* Intro & Outro content containers */
.introContainer {
  position: absolute;
  width: 640px;
  left: calc(50% - 320px);
  top: calc(50% - 150px);
  z-index: 1000;
  background: $darkBackgroundColor;
  color: $lightTextColor;
  box-shadow: $boxShadow;

  &.expanded {
    top: calc(50% - 180px);
  }
}
.outroContainer {
  position: fixed;
  bottom: 0;
  width: 100%;
  left: 0;
  z-index: 1000;
  background: $darkBackgroundColor;
  color: $lightTextColor;
}

/*** Vue transition: slide-fade ***/
.slide-fade-enter-active {
  transition: all $transitionDuration $transitionEasing $transitionDelay;
}
.slide-fade-leave-active {
  transition: all $transitionDuration $transitionEasing;
}
.slide-fade-enter, .slide-fade-leave-to  {
  transform: translateY(100px);
  opacity: 0;
}

/*** Vue transition: slide-fade-down ***/
.slide-fade-down-enter-active {
  transition: all $transitionDuration $transitionEasing $transitionDelay;
}
.slide-fade-down-leave-active {
  transition: all $transitionDuration $transitionEasing;
}
.slide-fade-down-enter, .slide-fade-down-leave-to  {
  transform: translateY(-100px);
  opacity: 0;
}

/* Search Box & Timeline */
.annotationContainer {
  position: absolute;
  bottom: 100px;
  left: 0;
  width: 25vw;
  background: white;
}
.searchText {
  font-size: 18px;
  font-weight: bold;
  font-variant: all-small-caps;
  margin: 1em 0 0.5em 1em;
}
.timelineContainer {
  position: absolute;
  bottom: 0;
  right: 0;
  width: calc(100% - 25px);
  background: $darkBackgroundColor;
  display: flex;
  justify-content: space-between;
  padding: 5px 0 5px 25px;
}

/* SCROLLING SECTION */
.sectionScrolling {
  text-align: right;
}

/* NOTE: the .waypoint elements are the elements
 * actually used by IntersetionObserver for showing
 * the intro and outro
 */
.waypoint {
  height: 120px;

  &.top {
    height: 45vh;
  }
}
.bloc {
  display: block;
  transition: color 0.5s $transitionEasing;
  color: #444;
  padding: 0 0.5em;
  font-size: 16px;
  /* Adjust margin-top to change scroll sensitivity */
  margin-bottom: 1.8em;
}
.bloc:first-child {
  /* Adjust padding and/or height to change time to first timeline update */
  padding-top: 300px;
  padding-bottom: 50px;
}
.bloc:last-child {
  padding-bottom: 250px;
  margin-bottom: 100vh;
}
.blocActive {
  font-weight: bold;
}


/* Mobile view (<900px wide) */
.mq-mobile {
  .mapContainer {
    .leaflet-control-zoom {
      display: none;
    }
    .leaflet-control-attribution {
        display: none;
    }
    &.halfHeight {
      .loader {
        top: 25%;
      }
    }
  }
  .introContainer {
    position: fixed;
    width: 100%;
    left: 0;
    right: 0;
    bottom: 0;
    top: unset;
  }
  .outroContainer {
    height: 100%;
    overflow: auto;
  }
  .mobileActionsContainer {
    position: absolute;
    bottom: calc(#{$tinelineContainerHeight} + #{$annotationHeight} + 1.5em);
    width: 100%;
  }
  .annotationContainer {
    position: absolute;
    z-index: 2000;
  }
  .logo {
    width: 100px;
    height: 35px;
    margin: 15px 30px;
  }
  .social {
    width: 100%;
  }
  .fullWidthContainer {
    left: 0;
    right: 0;
    width: 100%;

    &.top {
      top: 0;
    }
    &.bottom {
      bottom: 0;
    }
  }
  .timelineContainer {
    width: 100vw;
    left: 0;
    bottom: 12vh;
    padding: 5px 0 5px 0;
  }
}


/* Use for testing only
.debug {
  .sectionFixed, .outroContainer {
    width: 95vw;
  }
  // The .fixedIntersection element mimics the
  // viewport dimensions used by IntersetionObserver.
  .fixedIntersection {
    position: fixed;
    width: 10vw;
    top: 0;
    bottom: 97%;
    right: 0;
    border-top: 2px dotted #ffffff6b;
    border-bottom: 2px dotted #ffffff6b;
    background: #ff000033;
    z-index: 3000;
  }
  .waypoint {
    background: purple;
  }
  .bloc:last-child {
    background: blue;
  }
}
 */

</style>



// WEBPACK FOOTER //
// src/components/ScrollyMap.vue